// Production documents on purchase orders (ERP-PLAN §5.11): the spec revision each PO line carries, when a
// newer one has been released, and the PO's vendor documents link. Shared by the server (mutations, the
// documents page, the PDF, MCP) and the browser (PO panel), so both agree. Mutations: shared/mutations-po-docs.ts.
import type { PoStatus } from "./po.ts";

/** A documents link stops working this many days after its PO is closed or cancelled. */
export const DOC_LINK_DAYS = 30;
const DAY = 86_400_000;

/** When a PO's link expires after it moves to `status` (closing or cancelling starts the clock; reopening stops it). */
export const linkExpiresAt = (status: PoStatus, at = new Date()) =>
  status === "closed" || status === "cancelled" ? new Date(at.getTime() + DOC_LINK_DAYS * DAY) : null;

export type LinkState = "live" | "revoked" | "expired";
export function linkState(link: { revoked_at: string | Date | null; expires_at: string | Date | null }, now = Date.now()): LinkState {
  if (link.revoked_at) return "revoked";
  if (link.expires_at && new Date(link.expires_at).getTime() <= now) return "expired";
  return "live";
}

type Rev = { id: string; released_at: string | Date };

/**
 * The item's current revision if it's newer than the one a sent line carries, else null. A sent line without
 * one (its item had no released spec yet, or it was sent before lines carried revisions) only counts a revision
 * released after `since`: when the line was made (else when the PO was sent).
 */
export function newerRevision<R extends Rev>(carried: Rev | null | undefined, current: R | null | undefined, since?: string | Date | null): R | null {
  if (!current) return null;
  const at = (d: string | Date) => new Date(d).getTime();
  if (!carried) return since && at(current.released_at) > at(since) ? current : null;
  if (carried.id === current.id) return null;
  return at(current.released_at) > at(carried.released_at) ? current : null;
}

/** "12.4 MB" */
export function fileSize(bytes: number): string {
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 100 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

/** The documents page for a link token (relative; prefix the app's origin). */
export const docsPath = (token: string) => `/d/${token}`;
